import React from 'react';
import Text from '@amzn/meridian/text';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Breadcrumb, { BreadcrumbGroup } from '@amzn/meridian/breadcrumb';
import Responsive from '@amzn/meridian/responsive';
import SearchInput from 'src/components/common/search-input/SearchInput';
import UILoader from 'src/components/common/loader/UILoaderContainer';
import StatusAlert from 'src/components/common/status-alert/StatusAlertContainer';
import CleanContainerConfirmModal from 'src/components/pages/jackpot-page/clean-container-confirm-modal/CleanContainerConfirmModalContainer';
import AssignRoutingModal from 'src/components/pages/jackpot-page/assign-routing-modal/AssignRoutingModalContainer';
import JackpotScanLpnItemsTable from 'src/components/pages/jackpot-page/jackpot-scan-lpn/JackpotScanLpnItemsTable';
import JackpotScanToteInfoTable from 'src/components/pages/jackpot-page/jackpot-scan-lpn/JackpotScanToteInfoTable';
import * as AppStrings from 'src/localization/string-ids/app-strings';
import * as AppRoutes from 'src/routing/constants/routes';
import { responsiveProps } from 'src/constants/responsiveProps';
import { buildGetContainerDetailsInput, GetContainerDetailsInput, GetContainerMode } from 'src/models/GetContainerDetails';
import { ContainerDetailsModel } from 'src/components/common/container-details/ContainerDetailsModel';
import { ScanIcons } from 'src/constants/general-media';
import { AlertLevels } from 'src/models/alert-model';
import { UIAlertState } from 'src/store/common-slices/uiSlice';
import './JackpotScanLpn.scss';

interface JackpotScanLpnProps {
    containerDetails?: ContainerDetailsModel;
    uiAlertState: UIAlertState;
    toteDetails: ContainerDetailsModel;
    toteChildContainers: ContainerDetailsModel[];
    toteId: string;
    canCleanContainer: boolean;
    warehouseId: string;
    associate: string;
    locale: string;
    fetchContainerDetails: (getContainerDetailsInput: GetContainerDetailsInput) => Promise<unknown>;
    onCleanComplete: () => void;
    onAssignComplete: () => void;
}

interface JackpotScanLpnState {
    scannedItems: ContainerDetailsModel[];
    isCleanContainerModalOpen: boolean;
    isAssignRoutingModalOpen: boolean;
}

class JackpotScanLpn extends React.Component<JackpotScanLpnProps, JackpotScanLpnState> {

    constructor(props: JackpotScanLpnProps) {
        super(props);
        this.state = {
            scannedItems: [],
            isCleanContainerModalOpen: false,
            isAssignRoutingModalOpen: false
        };
    }

    private getCurrentToteDestination = (): string | null => {
        return this.props.toteDetails.containerProperties?.['routingDestination'] ?? null;
    }

    private getScannedItemDestinations = (): string[] => {
        const destinations = this.state.scannedItems
            .map(item => item.containerProperties?.['routingDestination'])
            .filter((destination): destination is string => !!destination);
        return Array.from(new Set(destinations));
    }

    private onItemSubmit = async (itemId: string) => {
        if (!itemId.trim()) {
            return;
        }
        await this.props.fetchContainerDetails(buildGetContainerDetailsInput(itemId,
            this.props.warehouseId, this.props.associate, this.props.locale,
            GetContainerMode.SEARCH));

        // Only add to the scanned list when the fetch actually succeeded — on error, the error
        // alert (rendered below via StatusAlert) shows on this page and nothing is appended.
        if (this.props.uiAlertState.alertLevel === AlertLevels.ERROR) {
            return;
        }
        const scannedItem = this.props.containerDetails;
        if (!scannedItem) {
            return;
        }
        this.setState(prevState => ({
            ...prevState,
            scannedItems: prevState.scannedItems.some(item => item.containerId === scannedItem.containerId)
                ? prevState.scannedItems
                : [...prevState.scannedItems, scannedItem]
        }));
    };

    private openAssignRoutingModal = () => {
        this.setState({ isAssignRoutingModalOpen: true });
    };

    private closeAssignRoutingModal = () => {
        this.setState({ isAssignRoutingModalOpen: false });
    };

    private openCleanContainerModal = () => {
        this.setState({ isCleanContainerModalOpen: true });
    };

    private closeCleanContainerModal = () => {
        this.setState({ isCleanContainerModalOpen: false });
    };

    public render() {
        return (
            <Responsive query={"max-width"} props={responsiveProps}>
                {props => (
                    <Column alignmentHorizontal="center">
                        <Column className="scan-lpn" alignmentHorizontal="center">
                            <BreadcrumbGroup>
                                <Breadcrumb href={AppRoutes.HOME_PAGE_ROUTE}>{AppStrings.HOME}</Breadcrumb>
                                <Breadcrumb>{AppStrings.JACKPOT}</Breadcrumb>
                            </BreadcrumbGroup>
                            <Column alignmentHorizontal="center">
                                <img src={ScanIcons.SCAN_HANDHELD_ICON} alt="Scan Icon"
                                     className="scan-lpn-icon"/>
                                <Text type={props.titleSize} data-testid="jackpot-scan-lpn-heading">{AppStrings.SCAN_ITEM_HEADING}</Text>
                            </Column>
                            <SearchInput
                                searchFieldLabel={AppStrings.SCAN_ITEM_SEARCHFIELD_LABEL}
                                searchFieldPlaceholder={AppStrings.SCAN_ITEM_SEARCHFIELD_PLACEHOLDER}
                                renderSearchButton={true}
                                onSubmit={this.onItemSubmit}
                                data-testid="jackpot-scan-lpn-search-input"
                            />
                        </Column>
                        <JackpotScanToteInfoTable toteDetails={this.props.toteDetails}/>
                        <Text data-testid="jackpot-tote-linked-item-count">
                            {AppStrings.ITEMS_LINKED_TO_TOTE_COUNT_LABEL}{this.props.toteChildContainers.length}
                        </Text>
                        <JackpotScanLpnItemsTable scannedItems={this.props.toteChildContainers}
                                                  dataTestId="jackpot-tote-linked-items-table"/>
                        <Text data-testid="jackpot-scan-lpn-item-count">
                            {AppStrings.ITEMS_SCANNED_COUNT_LABEL}{this.state.scannedItems.length}
                        </Text>
                        <JackpotScanLpnItemsTable scannedItems={this.state.scannedItems}/>
                        <Column className="scan-lpn" alignmentHorizontal="center">
                            <Row alignmentHorizontal="center">
                                <Button onClick={this.openAssignRoutingModal} data-testid="assign-routing-button">
                                    {AppStrings.ASSIGN_ROUTING_BUTTON}
                                </Button>
                                {this.props.canCleanContainer &&
                                    <Button onClick={this.openCleanContainerModal} data-testid="clean-container-button">
                                        {AppStrings.CLEAN_CONTAINER_BUTTON}
                                    </Button>
                                }
                            </Row>
                        </Column>
                        {this.state.isCleanContainerModalOpen &&
                            <CleanContainerConfirmModal
                                toteId={this.props.toteId}
                                onClose={this.closeCleanContainerModal}
                                onCleanComplete={this.props.onCleanComplete}
                            />
                        }
                        {this.state.isAssignRoutingModalOpen &&
                            <AssignRoutingModal
                                toteId={this.props.toteId}
                                currentToteDestination={this.getCurrentToteDestination()}
                                scannedItemDestinations={this.getScannedItemDestinations()}
                                onClose={this.closeAssignRoutingModal}
                                onAssignComplete={this.props.onAssignComplete}
                            />
                        }
                        <UILoader/>
                        <StatusAlert/>
                    </Column>
                )}
            </Responsive>
        );
    }
}

export default JackpotScanLpn;
